import React, { useState } from "react";
import "./Registration.css";

const services = [
  {
    number: "01",
    title: "Registration strategy",
    text: "We assess your service model, regulated activities, location and leadership structure before you apply.",
  },
  {
    number: "02",
    title: "Application support",
    text: "Guidance with provider, partner and registered manager forms, including accurate and consistent responses.",
  },
  {
    number: "03",
    title: "Statement of Purpose",
    text: "A professionally prepared Statement of Purpose aligned with your service, client group and regulated activities.",
  },
  {
    number: "04",
    title: "Policies & evidence",
    text: "A tailored evidence checklist and support with the core policies, procedures and governance documents CQC expects.",
  },
  {
    number: "05",
    title: "Registered manager coaching",
    text: "Preparation for the fit-person interview, including mock questions, feedback and improvement actions.",
  },
  {
    number: "06",
    title: "Submission & follow-up",
    text: "Final quality checks, submission guidance and support responding to CQC requests during assessment.",
  },
];

const supportCards = [
  {
    icon: "⌂",
    title: "Care homes",
    text: "Residential, nursing and specialist services",
  },
  {
    icon: "♡",
    title: "Homecare agencies",
    text: "Domiciliary care and supported living",
  },
  {
    icon: "+",
    title: "New providers",
    text: "First-time operators entering the sector",
  },
  {
    icon: "↻",
    title: "Existing providers",
    text: "New locations, activities and manager changes",
  },
];

const faqs = [
  {
    question: "When should I start preparing my CQC application?",
    answer:
      "Start before you are ready to operate. Your service model, premises, leadership, policies and evidence need to align. An early readiness review can identify gaps before they delay submission.",
  },
  {
    question: "Can you help with a registered manager application?",
    answer:
      "Yes. We can support registered manager applications, prepare documentation and provide interview coaching so you are confident before the CQC assessment.",
  },
  {
    question: "Do you support new locations and regulated activities?",
    answer:
      "Yes. We support existing providers with new locations, changes to regulated activities and registered manager changes.",
  },
  {
    question: "How long does CQC registration take?",
    answer:
      "The timeline varies depending on the application, evidence, completeness and CQC assessment process. We help you prepare correctly to reduce avoidable delays.",
  },
  {
    question: "Is registration approval guaranteed?",
    answer:
      "No consultant can guarantee CQC approval. Our role is to make your application as complete, consistent and well-prepared as possible.",
  },
];

function App() {
  const [activeFaq, setActiveFaq] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div className="site">
      {/* ================= HERO ================= */}
      <section className="hero" id="home">
        <div className="hero-container">
          <div className="hero-content reveal">
            <div className="eyebrow">
              <span></span>
              CQC REGISTRATION SUPPORT
            </div>

            <h1>
              Get CQC registered with
              <strong> clarity and confidence.</strong>
            </h1>

            <p>
              Expert, end-to-end support for new care providers, registered
              managers and existing services making changes to their
              registration.
            </p>

            <div className="hero-buttons">
              <a href="#contact" className="btn btn-primary">
                Book a free consultation <span>→</span>
              </a>

              <a href="#process" className="btn btn-secondary">
                See how it works <span>↓</span>
              </a>
            </div>

            <div className="hero-benefits">
              <span>✓ Care-sector specialists</span>
              <span>✓ Personal support</span>
              <span>✓ Fixed-fee packages</span>
            </div>
          </div>

          {/* Application Card */}
          <div className="application-wrapper reveal delay-2">
            <div className="floating-label">
              End-to-end
              <br />
              application support
            </div>

            <div className="application-card">
              <div className="application-header">
                <div>
                  <div className="small-title">
                    <i></i> REGISTRATION READINESS
                  </div>

                  <h3>Your CQC application, organised.</h3>
                </div>
              </div>

              <div className="progress">
                <span></span>
              </div>

              <div className="application-list">
                <div className="application-item">
                  <b>01</b>
                  <div>
                    <strong>Application review</strong>
                    <small>Provider & manager applications</small>
                  </div>
                  <em>✓</em>
                </div>

                <div className="application-item">
                  <b>02</b>
                  <div>
                    <strong>Statement of Purpose</strong>
                    <small>Clear, accurate and compliant</small>
                  </div>
                  <em>✓</em>
                </div>

                <div className="application-item">
                  <b>03</b>
                  <div>
                    <strong>Policies & evidence</strong>
                    <small>Built around your service</small>
                  </div>
                  <em>✓</em>
                </div>

                <div className="application-item">
                  <b>04</b>
                  <div>
                    <strong>Interview preparation</strong>
                    <small>Mock questions and coaching</small>
                  </div>

                  <label>In progress</label>
                </div>
              </div>

              <div className="advisor">
                <div className="advisor-avatar">WD</div>
                <div>
                  <strong>Dedicated adviser</strong>
                  <small>Support at every stage</small>
                </div>
                <span>•••</span>
              </div>
            </div>
          </div>
        </div>

        <div className="scroll-down">
          <div className="mouse"></div>
          <span>SCROLL DOWN</span>
        </div>
      </section>

      {/* ================= BENEFITS ================= */}
      <section className="benefit-strip">
        <div>
          <strong>Specialist</strong>
          <span>Care-sector knowledge</span>
        </div>

        <div>
          <strong>Practical</strong>
          <span>Hands-on application help</span>
        </div>

        <div>
          <strong>Personal</strong>
          <span>A dedicated adviser</span>
        </div>

        <div>
          <strong>Prepared</strong>
          <span>Interview coaching included</span>
        </div>
      </section>

      <div className="section-navigation">
        <a href="#services">What we do</a>
        <a href="#process">Our process</a>
        <a href="#pricing">Packages</a>
        <a href="#faq">FAQs</a>
      </div>

      {/* ================= SERVICES INTRO ================= */}
      <section className="services-intro">
        <div>
          <div className="eyebrow">
            <span></span>
            COMPLETE REGISTRATION SUPPORT
          </div>

          <h2>
            Everything you need to submit
            <br />a strong application
          </h2>
        </div>

        <p>
          We turn a complex registration process into a clear, managed plan —
          helping you prepare the right evidence and avoid preventable delays.
        </p>
      </section>

      {/* ================= SERVICES ================= */}
      <section className="services-section" id="services">
        <div className="services-grid">
          {services.map((service) => (
            <div className="service-card" key={service.number}>
              <div className="service-number">{service.number}</div>

              <h3>{service.title}</h3>

              <p>{service.text}</p>

              <span className="card-arrow">↗</span>
            </div>
          ))}
        </div>
      </section>

      {/* ================= PROCESS ================= */}
      <section className="process-section" id="process">
        <div className="process-container">
          <div className="process-content">
            <div className="eyebrow light">
              <span></span>A CLEAR ROUTE TO REGISTRATION
            </div>

            <h2>
              From first
              <br />
              conversation to
              <br />
              CQC decision
            </h2>

            <p>
              Your adviser coordinates each stage, keeps your documents
              consistent and makes sure you know what happens next.
            </p>

            <a href="#contact" className="btn process-btn">
              Start your registration <span>→</span>
            </a>
          </div>

          <div className="timeline">
            {[
              [
                "1",
                "Discovery & readiness review",
                "We understand your service and identify what is needed before submission.",
              ],
              [
                "2",
                "Build your application pack",
                "We prepare and review the forms, Statement of Purpose and supporting evidence.",
              ],
              [
                "3",
                "Prepare for assessment",
                "We coach the nominated individual or registered manager for the CQC interview.",
              ],
              [
                "4",
                "Submit & support",
                "You submit with confidence, with us available for questions and follow-up.",
              ],
            ].map((item) => (
              <div className="timeline-item" key={item[0]}>
                <div className="timeline-number">{item[0]}</div>

                <div>
                  <h4>{item[1]}</h4>
                  <p>{item[2]}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= WHO WE SUPPORT ================= */}
      <section className="support-section">
        <div className="section-heading">
          <div className="eyebrow">
            <span></span>
            WHO WE SUPPORT
          </div>

          <h2>Advice shaped around your service</h2>
        </div>

        <div className="support-grid">
          {supportCards.map((card) => (
            <div className="support-card" key={card.title}>
              <div className="support-icon">{card.icon}</div>

              <h3>{card.title}</h3>

              <p>{card.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ================= PRICING ================= */}
      <section className="pricing-section" id="pricing">
        <div className="pricing-heading">
          <div className="eyebrow center">STRAIGHTFORWARD PRICING</div>

          <h2>Choose the support that suits you</h2>

          <p>
            Every application is different. We confirm scope and fees after a
            free initial consultation.
          </p>
        </div>

        <div className="pricing-grid">
          <div className="pricing-card">
            <div className="plan-label">ESSENTIAL</div>

            <h3>Registration Support</h3>

            <div className="price">
              <small>From</small>
              £2,500
              <span>+ VAT</span>
            </div>

            <p>
              Structured expert support for providers who will complete parts of
              the application themselves.
            </p>

            <ul>
              <li>✓ Readiness consultation</li>
              <li>✓ Application form review</li>
              <li>✓ Statement of Purpose</li>
              <li>✓ Evidence checklist</li>
              <li>✓ Interview preparation</li>
            </ul>

            <a href="#contact" className="pricing-btn outline">
              Discuss this package
            </a>
          </div>

          <div className="pricing-card featured">
            <div className="most-popular">MOST COMPREHENSIVE</div>

            <div className="plan-label">FULL SUPPORT</div>

            <h3>Managed Registration</h3>

            <div className="price proposal">
              <small>Tailored</small>
              Proposal
            </div>

            <p>
              Hands-on, end-to-end support for complex applications or teams
              that want a managed process.
            </p>

            <ul>
              <li>✓ Everything in Essential</li>
              <li>✓ Application drafting support</li>
              <li>✓ Tailored policies and governance</li>
              <li>✓ Multiple coaching sessions</li>
              <li>✓ Ongoing CQC response support</li>
            </ul>

            <a href="#contact" className="pricing-btn">
              Get a tailored proposal
            </a>
          </div>
        </div>
      </section>

      {/* ================= FAQ ================= */}
      <section className="faq-section" id="faq">
        <div className="faq-left">
          <div className="eyebrow">
            <span></span>
            FREQUENTLY ASKED QUESTIONS
          </div>

          <h2>What providers ask us</h2>

          <p>
            Still unsure where to start? Book a free call and we’ll explain
            which route applies to your service.
          </p>

          <a href="#contact">Ask us a question →</a>
        </div>

        <div className="faq-list">
          {faqs.map((faq, index) => {
            const active = activeFaq === index;

            return (
              <div
                className={`faq-item ${active ? "active" : ""}`}
                key={faq.question}
              >
                <button onClick={() => setActiveFaq(active ? -1 : index)}>
                  <span>{faq.question}</span>
                  <b>{active ? "×" : "+"}</b>
                </button>

                {active && <div className="faq-answer">{faq.answer}</div>}
              </div>
            );
          })}
        </div>
      </section>

      {/* ================= CONTACT ================= */}
      <section className="contact-section" id="contact">
        <div className="contact-container">
          <div className="contact-content">
            <div className="eyebrow light">
              <span></span>
              LET’S GET YOU REGISTERED
            </div>

            <h3 className="Reg_text">
              Tell us about your care
              service
            </h3>

            <h4 className="Reg_Pera">
              Book a free initial consultation. We’ll discuss your plans,
              explain the likely registration route and recommend the right
              level of support.
            </h4>

            <div className="contact-benefits">
              <span>✓</span>
              No-obligation initial call
              <span>✓</span>
              Clear scope and fixed fees
              <span>✓</span>
              Specialist care-sector advice
            </div>
          </div>

          <form className="contact-form" onSubmit={(e) => e.preventDefault()}>
            <div className="form-row">
              <label>
                FULL NAME
                <input type="text" placeholder="Your name" />
              </label>

              <label>
                ORGANISATION
                <input type="text" placeholder="Company name" />
              </label>
            </div>

            <div className="form-row">
              <label>
                EMAIL ADDRESS
                <input type="email" placeholder="name@company.co.uk" />
              </label>

              <label>
                PHONE NUMBER
                <input type="tel" placeholder="Your number" />
              </label>
            </div>

            <label>
              SERVICE TYPE
              <select>
                <option>Select your service</option>
                <option>Care Home</option>
                <option>Homecare Agency</option>
                <option>Supported Living</option>
                <option>New Provider</option>
                <option>Existing Provider</option>
              </select>
            </label>

            <label>
              HOW CAN WE HELP?
              <textarea placeholder="Tell us where you are in the registration process..."></textarea>
            </label>

            <button type="submit" className="form-submit">
              Request my free consultation →
            </button>

            <small>
              By submitting this form, you agree that we may contact you about
              your enquiry.
            </small>
          </form>
        </div>
      </section>
    </div>
  );
}

export default App;
